/**
 * CentralRentalsEdmonton — dedicated landing at /central-rentals-edmonton
 * targeting the exact-match query "Central Rentals Edmonton".
 *
 * Purpose: Outrank the stale bbb.org/edmonton listing that currently
 * dominates the SERP for that query. Every ranking factor is stacked:
 *  - H1 + meta title + canonical all begin with "Central Rentals Edmonton"
 *  - LocalBusiness schema tied to our  ID
 *  - Same-day-updated address / phone / hours (24/7 online)
 *  - Deep internal links to signup + product pages
 *  - Reuses PropertyManagementCity's Edmonton content beneath the fold
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, MapPin, Phone, Mail, ShieldCheck, Building2, ArrowRight, Clock, FileText, CreditCard, Users } from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";

const CANONICAL = "https://centralrentals.ca/central-rentals-edmonton";
const TITLE = "Central Rentals Edmonton — Property Management Software";
const DESCRIPTION =
  "Central Rentals Edmonton is the Canadian-built property management platform for Edmonton landlords, tenants, and brokerages. RTA-compliant, PIPEDA-native, 24/7 online. Registered Alberta business (Corporate Registry ).";

function setMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-schema="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-schema", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function CentralRentalsEdmonton() {
  useEffect(() => {
    document.title = TITLE;
    setMeta('meta[name="description"]', { name: "description", content: DESCRIPTION });
    setMeta('meta[property="og:title"]', { property: "og:title", content: TITLE });
    setMeta('meta[property="og:description"]', { property: "og:description", content: DESCRIPTION });
    setMeta('meta[property="og:url"]', { property: "og:url", content: CANONICAL });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    setLink("canonical", CANONICAL);

    // LocalBusiness + Organization schema — 24/7 online, Edmonton HQ.
    const localBiz = {
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "Organization", "SoftwareApplication"],
      "@id": `${CANONICAL}#business`,
      name: "Central Rentals Edmonton",
      alternateName: ["Central Rentals Canada", "Central Rentals"],
      legalName: "Central Rentals Canada Inc.",
      url: CANONICAL,
      logo: "https://centralrentals.ca/logo.png",
      image: "https://centralrentals.ca/logo.png",
      description: DESCRIPTION,
      telephone: "+1-780-405-2498",
      email: "contact@centralrentals.ca",
      priceRange: "CA$35-250/month",
      areaServed: [
        { "@type": "City", name: "Edmonton" },
        { "@type": "AdministrativeArea", name: "Alberta" },
        { "@type": "Country", name: "Canada" },
      ],
      address: {
        "@type": "PostalAddress",
        addressLocality: "Edmonton",
        addressRegion: "AB",
        addressCountry: "CA",
      },
      geo: { "@type": "GeoCoordinates", latitude: 53.5461, longitude: -113.4938 },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
          opens: "00:00",
          closes: "23:59",
        },
      ],
      identifier: [
        {
          "@type": "PropertyValue",
          propertyID: "AlbertaCorporateRegistry",
          value: "",
          name: " ID",
        },
      ],
      award: [" — Active"],
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, iOS, Android",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        reviewCount: "127",
        bestRating: "5",
      },
      sameAs: [
        "https://centralrentals.ca",
        "https://maps.app.goo.gl/3R94HfmbLnbNgUvk9?g_st=ic",
        "https://maps.app.goo.gl/xNTfCYwvYybMADkT8",
        "https://maps.app.goo.gl/dvb9eQTLSW6VBhab7",
        "https://maps.app.goo.gl/Gv1trS78h4yjK3818",
        "https://maps.app.goo.gl/3WVMMqRoFMEmLcSr6",
        "https://www.linkedin.com/company/central-rentals-edmonton/",
        "https://albertaregistry.ca/search?q=Central+Rentals+Canada+Inc",
      ],
      // Direct citation to the Google Business Profile — helps Google
      // reconcile "central rentals canada" search intent with the
      // Maps entity so we appear in the Local Pack.
      hasMap: "https://maps.app.goo.gl/3R94HfmbLnbNgUvk9?g_st=ic",
    };
    setJsonLd("central-rentals-edmonton", localBiz);

    // FAQ schema — targets "who owns central rentals edmonton",
    // "central rentals edmonton hours" long-tail queries.
    const faq = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is Central Rentals Edmonton?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Central Rentals Edmonton is the Alberta-registered Canadian property management software company (Corporate ) headquartered in Edmonton. It provides rent collection, tenant screening, lease automation, and CRA-ready tax exports to landlords and property managers across all 13 Canadian provinces and territories.",
          },
        },
        {
          "@type": "Question",
          name: "What are Central Rentals Edmonton's hours?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Central Rentals is a 24/7 online SaaS platform — rent collection, tenant screening, and every feature run continuously, every day of the year including weekends. Phone support: Mon-Sat 09:00-20:00 MT and Sun 10:00-18:00 MT. Email support runs 24/7.",
          },
        },
        {
          "@type": "Question",
          name: "Is Central Rentals Edmonton a registered business?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Central Rentals Canada Inc. is registered with the  under ID  with Active status. The company is headquartered in Edmonton, Alberta.",
          },
        },
        {
          "@type": "Question",
          name: "How much does Central Rentals cost for Edmonton landlords?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Central Rentals starts at CA$35/month for the DIY tier (1-3 units) and scales to CA$79/month for property managers with unlimited units. Every plan includes RTA-compliant Alberta lease templates, Interac + PAD rent collection, and CRA T776 exports.",
          },
        },
      ],
    };
    setJsonLd("central-rentals-edmonton-faq", faq);

    // Breadcrumb
    const crumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://centralrentals.ca/" },
        { "@type": "ListItem", position: 2, name: "Edmonton", item: CANONICAL },
      ],
    };
    setJsonLd("central-rentals-edmonton-crumbs", crumbs);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader/>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <nav className="text-xs text-slate-500 flex items-center gap-2">
          <Link to="/" className="hover:text-slate-900">Home</Link>
          <span>/</span>
          <span className="text-slate-900 font-semibold">Central Rentals Edmonton</span>
        </nav>
        <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
          <ShieldCheck className="w-3.5 h-3.5"/>  · Active · 
        </div>
        <h1 className="mt-4 font-display text-3xl sm:text-5xl font-black tracking-tight text-slate-950 leading-[1.05]">
          Central Rentals Edmonton — the property management software for Alberta landlords.
        </h1>
        <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed max-w-3xl">
          Central Rentals Canada Inc. is an Edmonton-based Canadian SaaS company that builds property management software for landlords, tenants, and brokerages. RTA-compliant across all 13 provinces, PIPEDA-native, CAD-only pricing. Registered with the  (ID ).
        </p>

        {/* Fact strip — matches Google Knowledge Panel signals */}
        <div className="mt-8 grid sm:grid-cols-2 gap-3 text-sm">
          <FactRow icon={MapPin} label="Head office" value="Edmonton, Alberta, Canada" />
          <FactRow icon={Phone} label="Phone" value={<a href="tel:+17804052498" className="text-emerald-700 hover:underline">+1 (780) 405-2498</a>} />
          <FactRow icon={Mail} label="Email" value={<a href="mailto:contact@centralrentals.ca" className="text-emerald-700 hover:underline">contact@centralrentals.ca</a>} />
          <FactRow icon={Clock} label="Hours" value="24/7 online · phone Mon-Sat 9:00-20:00 MT · Sun 10:00-18:00 MT" />
          <FactRow icon={Building2} label="Legal entity" value="Central Rentals Canada Inc." />
          <FactRow icon={ShieldCheck} label="Corporate registry" value="Alberta  (Active)" />
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Link
            to="/signup"
            data-testid="cre-signup-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 text-white font-bold shadow-lg shadow-emerald-500/25 hover:bg-emerald-700 transition-colors"
          >
            Start free — cancel any time <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/pricing"
            data-testid="cre-pricing-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold transition-colors"
          >
            See Edmonton pricing
          </Link>
        </div>

        {/* Deep content — Edmonton-specific features, testimonials,
            and RTA links. Kept inline so the h2s live at this canonical. */}
        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-950">
            Built for Edmonton landlords, priced for Alberta.
          </h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {[
              { Icon: FileText, t: "Alberta RTA leases", d: "Draft an Alberta Residential Tenancies Act–compliant lease in 60 seconds. Every mandatory clause pre-filled, e-sign built-in." },
              { Icon: CreditCard, t: "Interac + PAD rent", d: "Collect rent via Interac e-Transfer or PAD (Pre-Authorized Debit) — the two payment rails Edmonton tenants actually use. CAD-native, T776-ready." },
              { Icon: Users, t: "Certn tenant screening", d: "Instant credit + eviction + reference checks for Alberta applicants — full Certn + Equifax report in under 60 seconds." },
              { Icon: ShieldCheck, t: "RTDRS-ready evictions", d: "Generate Alberta 14-Day Notice + Termination Notice + RTDRS filings with the correct forms — audit-trailed and PIPEDA-compliant." },
              { Icon: Building2, t: "Predictive maintenance", d: "Copilot studies your Edmonton repair history + neighbourhood patterns and flags upcoming issues before they escalate — average 22% lower annual repair spend." },
              { Icon: MapPin, t: "Alberta pricing", d: "Starts at CA$35/month for the DIY tier. Scales to CA$79/month for unlimited units. No per-door fees, no US pricing games." },
            ].map(({ Icon, t, d }) => (
              <div key={t} className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  <Icon className="w-5 h-5"/>
                </div>
                <div className="mt-3 font-bold text-slate-900">{t}</div>
                <div className="text-sm text-slate-600 mt-1 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ — mirrors the FAQPage schema so the answers stay identical
            between crawler and human readers. */}
        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-950">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-4">
            {[
              ["What is Central Rentals Edmonton?", "Central Rentals Edmonton is the Alberta-registered Canadian property management software company (Corporate ) headquartered in Edmonton. It provides rent collection, tenant screening, lease automation, and CRA-ready tax exports to landlords and property managers across all 13 Canadian provinces and territories."],
              ["What are Central Rentals Edmonton's hours?", "Central Rentals is a 24/7 online SaaS platform — rent collection, tenant screening, and every feature run continuously, every day of the year including weekends. Phone support: Mon-Sat 09:00-20:00 MT and Sun 10:00-18:00 MT. Email support runs 24/7."],
              ["Is Central Rentals Edmonton a registered business?", "Yes. Central Rentals Canada Inc. is registered with the  under ID  with Active status. The company is headquartered in Edmonton, Alberta."],
              ["How much does Central Rentals cost for Edmonton landlords?", "Central Rentals starts at CA$35/month for the DIY tier (1-3 units) and scales to CA$79/month for property managers with unlimited units. Every plan includes RTA-compliant Alberta lease templates, Interac + PAD rent collection, and CRA T776 exports."],
            ].map(([q, a]) => (
              <details key={q} className="group rounded-2xl border border-slate-200 bg-white p-5 open:border-emerald-200">
                <summary className="cursor-pointer list-none font-bold text-slate-900 flex items-start justify-between gap-4">
                  <span>{q}</span>
                  <span className="text-emerald-600 group-open:rotate-180 transition-transform text-xl leading-none">⌄</span>
                </summary>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="mt-14 rounded-3xl bg-slate-950 text-white p-8 sm:p-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[11px] font-bold uppercase tracking-wider">
              <MapPin className="w-3.5 h-3.5"/> Edmonton, Alberta
            </div>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight">
              Move your Edmonton rentals to a Canadian-built platform.
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">Start free. Import your properties in one CSV. Cancel any time.</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/signup" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-slate-900 font-bold hover:bg-slate-100 transition-colors" data-testid="cre-bottom-signup">
                Start free <ArrowRight className="w-4 h-4"/>
              </Link>
              <a href="tel:+17804052498" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/30 hover:bg-white/10 font-semibold transition-colors">
                <Phone className="w-4 h-4"/> +1 (780) 405-2498
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function FactRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <Icon className="w-4 h-4 mt-0.5 text-emerald-600 flex-none" />
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-sm text-slate-800 font-semibold mt-0.5 break-words">{value}</div>
      </div>
      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-none ml-auto mt-1" />
    </div>
  );
}
